"use client"

import { ArrowRight } from "lucide-react"
import { motion } from "motion/react"
import dynamic from "next/dynamic"
import Link from "next/link"

import { MotionBlurReveal } from "@/core/ui/motion-blur-reveal"
import { SpatialTextCascade } from "@/core/ui/spatial-text-cascade"
// Dynamic imports for heavy 3D components
const DigitalPresence = dynamic(() => import("@/core/ui/effects/DigitalPresence"), { ssr: false })

// New Section Imports
const Methodology = dynamic(() => import("@/modules/marketing/about/components/Methodology"), { ssr: false })
const TechArsenal = dynamic(() => import("@/modules/marketing/about/components/TechArsenal"), { ssr: false })
const Credentials = dynamic(() => import("@/modules/marketing/about/components/Credentials"), { ssr: false })
const GlobalReach = dynamic(() => import("@/modules/marketing/about/components/GlobalReach"), { ssr: false })
const Library = dynamic(() => import("@/modules/marketing/about/components/Library"), { ssr: false })
const PersonalBento = dynamic(() => import("@/modules/marketing/about/components/PersonalBento"), { ssr: false })
const Philosophy = dynamic(() => import("@/modules/marketing/about/components/Philosophy"), { ssr: false })

export default function AboutPage() {
  return (
    <div className="relative min-h-screen bg-black text-white selection:bg-violet-500/30">

      {/* --- HERO SECTION --- */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background 3D Digital Presence - accounts for navbar */}
        <div className="absolute inset-0 top-0 z-0">
          <DigitalPresence />
        </div>

        {/* Overlay Gradient */}
        <div className="absolute inset-0 z-10 bg-linear-to-b from-transparent via-black/50 to-black pointer-events-none" />

        {/* Content */}
        <div className="container relative z-20 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter">
                <MotionBlurReveal
                  text="Engineering"
                  tag="span"
                  mode="chars"
                  blurAmount={14}
                  stagger={0.03}
                  trigger="load"
                  className="block text-white justify-center"
                />
                <MotionBlurReveal
                  text="That Delivers"
                  tag="span"
                  mode="words"
                  blurAmount={12}
                  delay={0.25}
                  stagger={0.08}
                  trigger="load"
                  className="block text-transparent bg-clip-text bg-linear-to-r from-violet-400 via-purple-400 to-indigo-400 justify-center"
                />
              </h1>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              className="text-xl md:text-2xl text-slate-400 max-w-2xl mx-auto leading-relaxed"
            >
              I help founders and businesses turn ambitious ideas into fast, intelligent, and scalable software products that drive real revenue.
            </motion.p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">Scroll to Explore</span>
          <div className="w-px h-12 bg-linear-to-b from-violet-500 to-transparent" />
        </motion.div>
      </section>


      {/* --- PHILOSOPHY SECTION --- */}
      <Philosophy />

      {/* --- METHODOLOGY (THE BLUEPRINT) --- */}
      <Methodology />

      {/* --- TECH ARSENAL --- */}
      <TechArsenal />

      {/* --- CREDENTIALS --- */}
      <Credentials />

      {/* --- GLOBAL REACH --- */}
      <GlobalReach />

      {/* --- THE LIBRARY --- */}
      <Library />

      {/* --- PERSONAL BENTO --- */}
      <PersonalBento />

      {/* --- EXPLORE MORE SECTION --- */}
      <section className="relative py-20 px-4 md:px-6 bg-black">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <SpatialTextCascade
              text="Continue Exploring"
              tag="h2"
              alignment="center"
              mode="scrub"
              split="words"
              blurAmount={18}
              bloomTracking={true}
              className="text-2xl md:text-3xl font-bold text-white mb-4 justify-center"
            />
            <p className="text-slate-400">Dive deeper into how I work and what I&apos;ve built</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <Link
                href="/writing"
                className="block p-6 rounded-2xl border border-white/10 bg-white/5 hover:border-violet-500/50 hover:bg-violet-500/10 transition-all duration-300 group"
              >
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-violet-400 transition-colors">Writing &amp; Research</h3>
                <p className="text-slate-400 text-sm">Technical essays, architecture guides, and engineering standards</p>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-violet-400 mt-4 group-hover:translate-x-1 transition-all" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Link
                href="/services"
                className="block p-6 rounded-2xl border border-white/10 bg-white/5 hover:border-indigo-500/50 hover:bg-indigo-500/10 transition-all duration-300 group"
              >
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-indigo-400 transition-colors">Services</h3>
                <p className="text-slate-400 text-sm">AI Integration, Enterprise Automation, and Agentic Workflows</p>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-indigo-400 mt-4 group-hover:translate-x-1 transition-all" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <Link
                href="/work"
                className="block p-6 rounded-2xl border border-white/10 bg-white/5 hover:border-blue-500/50 hover:bg-blue-500/10 transition-all duration-300 group"
              >
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">Case Studies</h3>
                <p className="text-slate-400 text-sm">Real projects with measurable impact and outcomes</p>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-blue-400 mt-4 group-hover:translate-x-1 transition-all" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- CALL TO ACTION --- */}
      <section className="relative py-32 flex items-center justify-center overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-violet-950/20" />
        <div className="absolute inset-0 bg-linear-to-t from-black via-transparent to-black" />

        <div className="container relative z-10 text-center px-4">
          <motion.h2
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl font-bold mb-8"
          >
            Ready to Build the <span className="text-violet-400">Exceptional?</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link
              href="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black rounded-full font-bold text-lg hover:bg-violet-200 transition-colors duration-300"
            >
              Start the Conversation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
